import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import api from "../api/axiosInstance";
import { businessScopeParams } from "../utils/businessScope";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import useShippingStatus from "../hooks/useShippingStatus";
import {
    orderNumber,
    formatPlaced,
    shippingChip,
    progressFor,
    hasShipment,
    panelFor,
    isSellerSession,
    orderLines,
    orderTotals,
    packageFrom,
    ShipmentProgress,
    ShippingSummary,
    CourierToShip,
    CourierBooked,
    CourierMoving,
    ManualShipping,
    useFreshTracking,
    CustomerCard,
    ItemsCard,
} from "./orderDetails";
import "datatables.net-dt/css/jquery.dataTables.css";

function OrderManagementview() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const EventId = searchParams.get("id");
    const [eventDetails, setEventDetails] = useState(null);
    // "not_found" | "failed" | null
    const [loadError, setLoadError] = useState(null);
    const [greetingCardDetails, setGreetingCardDetails] = useState([]);
    const [giftDetails, setGiftDetails] = useState([]);
    // The store's shipping setup: a courier store books shipments itself
    const shipping = useShippingStatus();
    // Where the courier collects from: the seller's own settings (the public store info leaves the address out)
    const [pickupAddress, setPickupAddress] = useState("");
    useEffect(() => {
        if (!isSellerSession()) return undefined;
        let live = true;
        Promise.resolve(api.get("/shipping/settings"))
            .then((res) => {
                const p = res && res.data && res.data.result && res.data.result.pickup_address;
                if (live && p) setPickupAddress([p.name, p.address, [p.city, p.pincode].filter(Boolean).join(" ")].filter(Boolean).join(", "));
            })
            .catch(() => {});
        return () => {
            live = false;
        };
    }, []);
    useEffect(() => {
        if (!EventId) {
            navigate(`/ordermanagement`);
        } else {
            fetchData();
        }
    }, [EventId]);

    // One placed order of this store (uniquegiftapi GET /event/order/details):
    // /event/list answers only the shopper who owns the cart. The store comes
    // from its login, as on the order list (utils/businessScope).
    const fetchData = () => {
        setLoadError(null);
        api
            .get('/event/order/details', { params: { event_id: EventId, ...businessScopeParams() } })
            .then((res) => {
                if (res.data && res.data.status === "success") {
                    setEventDetails(res.data.result);
                    fetchGreetingAndGiftDetails(res.data.result.cart);
                } else {
                    setLoadError("not_found");
                }
            })
            .catch((err) => {
                console.error("Error fetching events:", err);
                setLoadError("failed");
            });
    };


    const fetchGreetingAndGiftDetails = (cartItems) => {
        const greetingCardIds = cartItems
            .filter((item) => item.greeting_card)
            .map((item) => {
                return {
                    greeting_card: item.greeting_card,
                    order_data_count: item.order_data_count,
                };
            });

        const giftIds = cartItems
            .filter((item) => item.gift)
            .map((item) => {
                return {
                    gift: item.gift,
                    order_data_count: item.order_data_count,
                    selected_variants: item.selected_variants || [], // Include selected variants
                };
            });

        // Create arrays to store the results
        let greetingCards = [];
        let gifts = [];

        // Fetch greeting cards data
        const greetingCardPromises = greetingCardIds.map((item) => {
            return api
                .get('/greeting/list', { params: { greeting_id: item.greeting_card } })
                .then((res) => {
                    const updatedResult = {
                        ...res.data.result,
                        orderCount: item.order_data_count,  // Add item.order to the response
                    };
                    greetingCards.push(updatedResult); // Collect results
                })
                .catch((err) => {
                    console.error("Error fetching greeting card details for ID:", item.greeting_card, err);
                });
        });

        // Fetch gift data
        const giftPromises = giftIds.map((item) => {
            return api
                .get('/gift/list', { params: { gift_id: item.gift } })
                .then((res) => {
                    const updatedResult = {
                        ...res.data.result,
                        orderCount: item.order_data_count,  // Add item.order to the response
                        selected_variants: item.selected_variants, // Add selected variants
                    };
                    gifts.push(updatedResult); // Collect results
                })
                .catch((err) => {
                    console.error("Error fetching gift details for ID:", item.gift, err);
                });
        });

        // Once all data is fetched, update the state in a single call
        Promise.all([...greetingCardPromises, ...giftPromises]).then(() => {
            setGreetingCardDetails(greetingCards);  // Set all greeting card details at once
            setGiftDetails(gifts);  // Set all gift details at once
        });
    };


    // The API found no such order for this panel (another store's order, or
    // one not placed yet), or the request failed: say so instead of loading forever
    // A moving parcel's latest tracking, when it wasn't checked lately
    useFreshTracking(eventDetails, fetchData);

    if (loadError) {
        return (
            <div className="wrapper">
                <header>
                    <NavigationBar />
                </header>
                <div className="content-wrapper">
                    <div className="content">
                        <div className="container-fluid pt-4">
                            <div className="card card-outline card-warning">
                                <div className="card-body text-center py-5" role="alert">
                                    <h4 className="mb-2">{loadError === "not_found" ? "Order not found" : "Couldn't load this order"}</h4>
                                    <p className="text-muted mb-4">
                                        {loadError === "not_found"
                                            ? "This order doesn't exist, isn't from your store, or hasn't been placed yet."
                                            : "Please check your connection and try again."}
                                    </p>
                                    {loadError !== "not_found" && (
                                        <button type="button" className="btn btn-primary mr-2" onClick={fetchData}>Try again</button>
                                    )}
                                    <a href="/ordermanagement" className="btn btn-outline-secondary">
                                        <i className="fa-solid fa-arrow-left-long mr-2" />
                                        Back to orders
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!eventDetails) {
        return (
            <div className="wrapper" id="root">
                <header>
                    <NavigationBar />
                </header>
                <div
                    className="content-wrapper d-flex justify-content-center align-items-center"
                    style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}
                >
                    <div
                        style={{
                            border: "6px solid #f3f3f3",
                            borderTop: "6px solid #3498db",
                            borderRadius: "50%",
                            width: "50px",
                            height: "50px",
                            animation: "spin 1s linear infinite",
                        }}
                    />
                    <style>
                        {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
                    </style>
                </div>
                <Footer />
            </div>
        );
    }

    const status = eventDetails.current_tracking_status || "ordered";
    const cancelled = status === "cancelled" || eventDetails.purchase_status === "cancelled";
    const booked = hasShipment(eventDetails);
    const panel = panelFor(eventDetails, !!shipping.isCourier);
    // Shown as a courier order: booked with one, or a courier store's order still to ship
    const isCourier = booked || panel === "toShip" || (panel === "readonly" && !!shipping.isCourier);
    const providerLabel = (shipping.isCourier && shipping.providerLabel) || eventDetails.courier_service || "the courier";
    const chip = shippingChip(eventDetails);
    const placed = formatPlaced(eventDetails.created_date);
    const lines = orderLines([...giftDetails, ...greetingCardDetails]);
    const totals = orderTotals(eventDetails, lines);
    const progress = progressFor(eventDetails, isCourier);
    const shipmentStatus = (eventDetails.shipment || {}).status;

    return (
        <div className="wrapper">
            <header>
                <NavigationBar />
            </header>

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="mtk-order-head">
                            <div className="mtk-order-head__title">
                                <h1 className="m-0">Order #{orderNumber(eventDetails._id)}</h1>
                                <span className={`mtk-order-chip mtk-order-chip--${chip.tone}`} data-testid="order-chip">{chip.label}</span>
                                {placed && <span className="mtk-order-muted">Placed {placed}</span>}
                            </div>
                            <a href="/ordermanagement" className="mtk-order-back">
                                <span aria-hidden="true">← </span>Back to orders
                            </a>
                        </div>
                    </div>
                </div>

                <div className="content">
                    <div className="container-fluid">
                        <div className="mtk-order-layout">
                            <section className="mtk-order-card mtk-order-ship" aria-labelledby="order-shipping-title">
                                <div className="mtk-order-ship__head">
                                    <h2 id="order-shipping-title">Shipping</h2>
                                    <span className="mtk-order-muted">
                                        {isCourier ? `Courier: ${providerLabel} (your account)` : "You ship this order yourself"}
                                    </span>
                                </div>
                                <div className="mtk-order-ship__body">
                                    <ShipmentProgress
                                        steps={progress.steps}
                                        step={progress.step}
                                        tone={["IN_TRANSIT", "OUT_FOR_DELIVERY"].includes(shipmentStatus) || ["in_transit", "out_for_delivery"].includes(status) ? "blue" : "green"}
                                    />
                                    {["toShip", "manual", "none", "readonly"].includes(panel) && (
                                        <ShippingSummary
                                            order={eventDetails}
                                            totals={totals}
                                            pickup={eventDetails.pickup_address || pickupAddress}
                                            courier={isCourier}
                                            shippingKnown={shipping.state !== "unknown"}
                                        />
                                    )}
                                    {cancelled && <p className="mtk-order-hint" role="note">This order is cancelled, so it can't be shipped.</p>}
                                    {eventDetails.shipment && eventDetails.shipment.conflict && (
                                        <div className="alert alert-danger py-2" role="alert" data-testid="shipment-conflict">{eventDetails.shipment.conflict}</div>
                                    )}
                                    {panel === "toShip" && (
                                        <CourierToShip
                                            order={eventDetails}
                                            providerLabel={providerLabel}
                                            initialPackage={packageFrom(lines)}
                                            onUpdated={fetchData}
                                        />
                                    )}
                                    {panel === "booked" && <CourierBooked order={eventDetails} providerLabel={providerLabel} onUpdated={fetchData} />}
                                    {panel === "moving" && <CourierMoving order={eventDetails} providerLabel={providerLabel} onUpdated={fetchData} />}
                                    {panel === "readonly" && (
                                        <p className="mtk-order-hint" role="note" data-testid="courier-readonly">
                                            {booked
                                                ? `Shipping with ${providerLabel}, AWB ${eventDetails.shipment.awb}. The store books, labels and tracks it from its own login.`
                                                : `The store ships this order with ${providerLabel} from its own login.`}
                                        </p>
                                    )}
                                    {panel === "manual" && <ManualShipping order={eventDetails} onUpdated={fetchData} />}
                                </div>
                            </section>

                            <div className="mtk-order-side">
                                <CustomerCard order={eventDetails} />
                                <ItemsCard
                                    order={eventDetails}
                                    lines={lines}
                                    totals={totals}
                                    shippingLabel="Shipping"
                                />
                                <section className="mtk-order-note">
                                    Courier charges and COD money are settled between you and your courier. TamilKart only connects the two.
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}

export default OrderManagementview;
